import { makeStyles, Grid } from '@material-ui/core';
import React, { useMemo, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import fp from 'lodash/fp';

import FormioForm from './components/FormioForm';
import ValidationMessages from './components/ValidationMessages';
import {
  allowLocalDevFileUpload,
  disableComponentDataCache,
  prepareSubmissions,
  sanitizeComponent,
  transformComponents,
} from './utils';
import { FormComponent, FormSubmission, FormValidationError } from './types';

import styles from './Form.styles';
import { ValidationMessagesProps } from './components/ValidationMessages/ValidationMessages';

export interface Props {
  onSubmit: (submission: FormSubmission) => void;
  onChange?: (submission: FormSubmission) => void;
  language: string,
  components?: Array<FormComponent>;
  submissionData?: FormSubmission;
  validationErrors?: Array<FormValidationError>;
  readOnly?: boolean;
  parentPath?: string;
  validationMessagesProps?: ValidationMessagesProps;
  evalContext?: Record<string, unknown>;
  fileServiceNonSupport?: boolean;
  onCustomEvent?: (options: { type: string, data: FormSubmission }) => void;
  componentsTransformer?: (component: FormComponent) => FormComponent;
  formRef?: React.Ref<unknown>;
  onDirtyChange?: (status?: boolean) => void;
  fileMaxSize?: string,
  fileMaxTotalSize?: string,
  blackList?: string[],
  skipSubmissionTransformations?: boolean;
}

const useStyles = makeStyles(styles, { name: 'Form' });

export default function Form({
  language,
  components: componentsProps = [],
  onSubmit,
  submissionData,
  validationErrors = [],
  readOnly = false,
  validationMessagesProps = {},
  evalContext,
  onCustomEvent,
  parentPath,
  fileServiceNonSupport,
  componentsTransformer = (c) => c,
  onChange,
  formRef,
  onDirtyChange,
  fileMaxSize,
  fileMaxTotalSize,
  blackList,
  skipSubmissionTransformations,
}: Props) {
  const classes = useStyles();
  const messages = validationErrors
    .filter(({ message }) => !!message)
    .map(({ message }) => message) as Array<string>;
  const submission = useMemo(() => {
    return !isEmpty(submissionData?.data) ? cloneDeep(submissionData) : undefined;
  }, [submissionData]);
  const components = useMemo(() => {
    const transformer = fp.compose(
      disableComponentDataCache,
      allowLocalDevFileUpload,
      sanitizeComponent,
      componentsTransformer,
    );

    return transformComponents(componentsProps, transformer);
  }, [componentsTransformer, componentsProps]);

  const handleSubmit = useCallback((formSubmission: FormSubmission) => {
    const formData = skipSubmissionTransformations
      ? formSubmission
      : prepareSubmissions(components, formSubmission, { isFormData: true }) || { data: {} };
    onSubmit({
      ...formData,
      storageData: get(
        prepareSubmissions(components, formSubmission, { forStorage: true, isFormData: true }),
        'data',
        {},
      ),
    });
  }, [components, onSubmit, skipSubmissionTransformations]);

  const handleCustomEvent = useCallback((options: { type: string, data?: Record<string, unknown> }) => {
    const formData = skipSubmissionTransformations
      ? { data: options.data || {} }
      : prepareSubmissions(components, { data: options.data || {} }, { isFormData: true }) as FormSubmission;
    if (onCustomEvent) {
      onCustomEvent({
        data: {
          ...formData,
          storageData: get(
            prepareSubmissions(components, { data: options.data || {} }, { forStorage: true, isFormData: true }),
            'data',
            {},
          ),
        },
        type: options.type,
      });
    }
  }, [components, onCustomEvent, skipSubmissionTransformations]);

  const preparedSubmissionData = useMemo(
    () => {
      if (skipSubmissionTransformations) {
        return submission;
      }
      return prepareSubmissions(components, submission);
    },
    [components, submission, skipSubmissionTransformations],
  );

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item sm={8}>
          <ValidationMessages messages={messages} {...validationMessagesProps} />
        </Grid>
      </Grid>
      {
        components.length ? (
          <FormioForm
            // key is needed for preventing to submit form multiple times while complete task
            key={messages.length}
            formRef={formRef}
            components={components}
            readOnly={readOnly}
            onSubmit={handleSubmit}
            onCustomEvent={handleCustomEvent}
            submission={preparedSubmissionData}
            language={language}
            evalContext={evalContext}
            parentPath={parentPath}
            fileServiceNonSupport={fileServiceNonSupport}
            onChange={onChange}
            onDirtyChange={onDirtyChange}
            fileMaxSize={fileMaxSize}
            fileMaxTotalSize={fileMaxTotalSize}
            blackList={blackList}
          />
        ) : ''
      }
    </div>
  );
}
